import { bottomChromeSlotsAtom } from "@/atoms/bottom-chrome";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";

/**
 * Publishes how much ONE fixed footer takes from the screen bottom into the
 * registry floating content reads. Measures `innerHeight - rect.top`, not bar
 * height alone (floating bag sits 16px above the floor). `id` must be stable
 * and unique per bar.
 */
export function useBottomChromeSlot<T extends HTMLElement>(id: string) {
  const setSlots = useSetAtom(bottomChromeSlotsAtom);
  const nodeRef = useRef<T | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const clear = useCallback(() => {
    setSlots((slots) => {
      if (!(id in slots)) return slots;
      const next = { ...slots };
      delete next[id];
      return next;
    });
  }, [id, setSlots]);

  const measure = useCallback(() => {
    const node = nodeRef.current;
    if (!node) return;
    const occupied = Math.max(
      0,
      window.innerHeight - node.getBoundingClientRect().top,
    );
    setSlots((slots) =>
      slots[id] === occupied ? slots : { ...slots, [id]: occupied },
    );
  }, [id, setSlots]);

  const measureRef = useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      nodeRef.current = node;

      if (!node) {
        clear();
        return;
      }

      const observer = new ResizeObserver(measure);
      observer.observe(node);
      observerRef.current = observer;
      measure();
    },
    [clear, measure],
  );

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      clear();
    },
    [clear],
  );

  return measureRef;
}
